/**
 * Role Service - V3 Schema with standardHours
 *
 * Implements V3 Role operations with billing integration:
 * - createRole(input): Create role with standardHours and permissions
 * - seedPresetRoles(): Create super-admin, admin, user roles
 * - validateRole(role): Validate standardHours range, permissions array
 * - getRoleById(roleId): Fetch role document
 * - hasPermission(role, permission): Check if role has permission
 *
 * Key V3 Changes:
 * - Removed: level (numeric hierarchy)
 * - Removed: isExemptFromHours (use standardHours: 0)
 * - standardHours supports signed half-hours for billing
 * - Added: permissions (granular capability array)
 * - Added: systemRole ("super-admin" | "admin" | "user")
 *
 * @see specs/datamodel-refactor/data-model.md for V3 schema specification
 */

import {
  type Firestore,
  type QueryDocumentSnapshot,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore"

import type {
  V3Role,
  V3RoleCreateInput,
  V3RoleUpdateInput,
} from "./types/v3-entities"
import { validateV3Role } from "./types/v3-entities"
import { assertOptionalHourValue } from "./v3-field-validation"

// ============================================================================
// Preset Role Definitions
// ============================================================================

/**
 * Preset roles for V3 system.
 * These are seeded on system initialization.
 */
export const V3_PRESET_ROLES: Record<string, V3Role> = {
  "super-admin": {
    id: "super-admin",
    displayName: "Superadministrator",
    description: "Systemadministrator med alle tillatelser",
    systemRole: "super-admin",
    standardHours: 0,
    permissions: ["*"],
  },
  admin: {
    id: "admin",
    displayName: "Administrator",
    description: "Organisasjonsadministrator",
    systemRole: "admin",
    standardHours: 0,
    permissions: [
      "users:read",
      "users:write",
      "persons:read",
      "persons:write",
      "tasks:read",
      "tasks:write",
      "tasks:assign",
      "billing:read",
      "billing:write",
      "billing:export",
      "organization-hours:read",
      "settings:read",
      "settings:write",
      "teams:manage",
      "activityTypes:manage",
      "campaigns:send",
      "automation:manage",
    ],
  },
  user: {
    id: "user",
    displayName: "Bruker",
    description: "Bruker med timekrav",
    systemRole: "user",
    standardHours: 20, // Positive = 20 hour requirement per child
    permissions: ["users:read", "persons:read", "tasks:read", "tasks:assign"],
  },
}

// ============================================================================
// Collection References
// ============================================================================

// Compatibility helpers for existing callers. Role names are administrator
// data, not product terminology. Preserve them and the transactional source.
export const getUnprojectedRoleSource = <T extends object>(role: T): T =>
  role

/** Preserve every saved role name, including names on preset roles. */
export function withCurrentRoleTerminology<
  T extends {
    id: string
    systemRole?: string
    displayName: string
    description?: string
  },
>(role: T): T {
  return role
}

/**
 * Gets the roles collection reference.
 * Storage: public/data/roles
 */
export const getV3RolesCollectionRef = (db: Firestore) =>
  collection(db, "public/data/roles")

// ============================================================================
// Role CRUD Operations
// ============================================================================

const assertOptionalSystemRole = (systemRole: string | undefined) => {
  if (systemRole && !["super-admin", "admin", "user"].includes(systemRole)) {
    throw new Error("systemRole must be 'super-admin', 'admin', or 'user'")
  }
}

/**
 * Creates a new V3 Role with standardHours and permissions.
 *
 * @param db - Firestore database instance
 * @param input - Role creation input
 * @param roleId - Optional custom role ID (for preset roles)
 * @returns The role ID
 * @throws Error if validation fails
 */
export const createV3Role = async (
  db: Firestore,
  input: V3RoleCreateInput,
  roleId?: string,
): Promise<string> => {
  // Validate displayName
  if (!input.displayName || input.displayName.trim() === "") {
    throw new Error("displayName is required")
  }

  // Undefined standardHours inherit the global setting.
  assertOptionalHourValue(input.standardHours, "standardHours")
  if (!Array.isArray(input.permissions)) {
    throw new Error("permissions must be an array")
  }
  assertOptionalSystemRole(input.systemRole)

  const roleData: Omit<V3Role, "id"> = {
    displayName: input.displayName.trim(),
    description: input.description,
    systemRole: input.systemRole,
    standardHours: input.standardHours,
    permissions: input.permissions,
  }

  if (roleId) {
    // Use specific ID for preset roles
    const roleRef = doc(getV3RolesCollectionRef(db), roleId)
    await setDoc(roleRef, roleData)
    return roleId
  }

  // Auto-generate ID for custom roles
  const roleRef = doc(getV3RolesCollectionRef(db))
  await setDoc(roleRef, roleData)
  return roleRef.id
}

/**
 * Seeds all preset roles (super-admin, admin, user).
 * Skips roles that already exist.
 *
 * @param db - Firestore database instance
 * @returns Array of created role IDs
 */
export const seedPresetRoles = async (db: Firestore): Promise<string[]> => {
  const createdIds: string[] = []

  for (const [roleId, roleData] of Object.entries(V3_PRESET_ROLES)) {
    const existingRole = await getV3RoleById(db, roleId)
    if (existingRole) {
      // Role already exists, skip
      continue
    }

    await createV3Role(
      db,
      {
        displayName: roleData.displayName,
        description: roleData.description,
        systemRole: roleData.systemRole,
        standardHours: roleData.standardHours,
        permissions: roleData.permissions,
      },
      roleId,
    )
    createdIds.push(roleId)
  }

  return createdIds
}

/**
 * Gets a role by ID.
 *
 * @param db - Firestore database instance
 * @param roleId - The role's document ID
 * @returns The role or null if not found
 */
export const getV3RoleById = async (
  db: Firestore,
  roleId: string,
): Promise<V3Role | null> => {
  const roleRef = doc(getV3RolesCollectionRef(db), roleId)
  const roleSnap = await getDoc(roleRef)

  if (!roleSnap.exists()) {
    return null
  }

  return withCurrentRoleTerminology({
    id: roleSnap.id,
    ...roleSnap.data(),
  } as V3Role)
}

/**
 * Gets all roles.
 *
 * @param db - Firestore database instance
 * @returns Array of all roles
 */
export const getAllV3Roles = async (db: Firestore): Promise<V3Role[]> => {
  const rolesSnapshot = await getDocs(query(getV3RolesCollectionRef(db)))
  return rolesSnapshot.docs.map(
    (docSnap: QueryDocumentSnapshot) =>
      withCurrentRoleTerminology({
        id: docSnap.id,
        ...docSnap.data(),
      } as V3Role),
  )
}

/**
 * Updates a role.
 *
 * @param db - Firestore database instance
 * @param roleId - The role's document ID
 * @param updates - Partial role data to update
 */
export const updateV3Role = async (
  db: Firestore,
  roleId: string,
  updates: V3RoleUpdateInput,
): Promise<void> => {
  assertOptionalHourValue(updates.standardHours, "standardHours")
  if (
    updates.permissions !== undefined &&
    !Array.isArray(updates.permissions)
  ) {
    throw new Error("permissions must be an array")
  }
  assertOptionalSystemRole(updates.systemRole)

  const roleRef = doc(getV3RolesCollectionRef(db), roleId)
  await updateDoc(roleRef, updates)
}

/**
 * Deletes a role.
 * Note: Should check if any users are assigned to this role first.
 *
 * @param db - Firestore database instance
 * @param roleId - The role's document ID
 */
export const deleteV3Role = async (
  db: Firestore,
  roleId: string,
): Promise<void> => {
  // Prevent deletion of preset system roles
  if (V3_PRESET_ROLES[roleId]) {
    throw new Error(`Cannot delete preset system role: ${roleId}`)
  }

  const roleRef = doc(getV3RolesCollectionRef(db), roleId)
  await deleteDoc(roleRef)
}

/**
 * Validates a V3 Role object.
 * Re-export from v3-entities for convenience.
 */
export { validateV3Role }

// ============================================================================
// Permission Helpers
// ============================================================================

import type { Permission } from "./types/permissions"

/**
 * Checks if a role has a specific permission.
 * Handles undefined/null permissions arrays defensively.
 *
 * @param role - The role to check (can have undefined permissions)
 * @param permission - The permission string (e.g., "users:read")
 * @returns true only for an explicitly stored non-wildcard permission
 */
export const hasPermission = (
  role: { permissions?: string[] } | null | undefined,
  permission: Permission | string,
): boolean => {
  // Defensive: handle null/undefined role
  if (!role) {
    return false
  }

  // Defensive: handle undefined/null permissions array
  const permissions = role.permissions
  if (!permissions || !Array.isArray(permissions)) {
    return false
  }

  // Platform authority belongs to the trusted Auth claim, never a tenant role.
  return permission !== "*" && permissions.includes(permission)
}

/**
 * Checks if a role has any of the specified permissions.
 * Handles undefined/null permissions arrays defensively.
 *
 * @param role - The role to check (can have undefined permissions)
 * @param permissions - Array of permission strings to check
 * @returns true if role has at least one of the permissions
 */
export const hasAnyPermission = (
  role: { permissions?: string[] } | null | undefined,
  permissions: (Permission | string)[],
): boolean => {
  // Defensive: handle null/undefined role
  if (!role) {
    return false
  }

  // Defensive: handle undefined/null permissions array
  const rolePermissions = role.permissions
  if (!rolePermissions || !Array.isArray(rolePermissions)) {
    return false
  }

  return permissions.some((p) => p !== "*" && rolePermissions.includes(p))
}

/**
 * Checks if a role has all of the specified permissions.
 * Handles undefined/null permissions arrays defensively.
 *
 * @param role - The role to check (can have undefined permissions)
 * @param permissions - Array of permission strings to check
 * @returns true if role has all of the permissions
 */
export const hasAllPermissions = (
  role: { permissions?: string[] } | null | undefined,
  permissions: (Permission | string)[],
): boolean => {
  // Defensive: handle null/undefined role
  if (!role) {
    return false
  }

  // Defensive: handle undefined/null permissions array
  const rolePermissions = role.permissions
  if (!rolePermissions || !Array.isArray(rolePermissions)) {
    return false
  }

  return permissions.every((p) => p !== "*" && rolePermissions.includes(p))
}

// ============================================================================
// Billing Helpers
// ============================================================================

/**
 * Gets the standardHours for a role.
 * Positive = requirement, Negative = credit, Zero = neutral.
 *
 * @param role - The role
 * @returns The standardHours value, or undefined if inheriting from global settings
 */
export const getRoleStandardHours = (role: V3Role): number | undefined => {
  return role.standardHours
}

/**
 * Checks if a role is exempt from hours (standardHours === 0).
 * Note: undefined standardHours means inherit from global settings (not exempt).
 *
 * @param role - The role to check
 * @returns true if role has no hour requirement or credit (explicit 0)
 */
export const isHoursExempt = (role: V3Role): boolean => {
  return role.standardHours === 0
}

/**
 * Checks if a role has hour credit (negative standardHours).
 * Note: undefined standardHours means inherit from global settings.
 *
 * @param role - The role to check
 * @returns true if role provides hour credit
 */
export const hasHourCredit = (role: V3Role): boolean => {
  return role.standardHours !== undefined && role.standardHours < 0
}

/**
 * Checks if a role has hour requirement (positive standardHours).
 * Note: undefined standardHours means inherit from global settings.
 *
 * @param role - The role to check
 * @returns true if role has hour requirement
 */
export const hasHourRequirement = (role: V3Role): boolean => {
  return role.standardHours !== undefined && role.standardHours > 0
}
